<script lang="ts">
  import { Badge, Skeleton } from '@aatmanova/ui';
  import OrderDrawer from './OrderDrawer.svelte';

  interface Props {
    orders: any[];
    ordersQueryIsLoading: boolean;
    ordersMeta?: {
      page: number;
      limit: number;
      total: number;
      totalPages: number;
    };
    client: any;
    addToast: (message: string, type?: 'info' | 'success' | 'warning' | 'error') => void;
    invalidateOrders: () => void;
    page: number;
    limit: number;
    orderStatus: string;
  }

  let {
    orders,
    ordersQueryIsLoading,
    ordersMeta,
    client,
    addToast,
    invalidateOrders,
    page = $bindable(),
    limit = $bindable(),
    orderStatus = $bindable(),
  }: Props = $props();

  let selectedOrder = $state<any>(null);
  let isDrawerOpen = $state(false);

  const STATUS_OPTIONS = ['ALL', 'PENDING', 'PROCESSING', 'SHIPPED', 'DELIVERED', 'CANCELLED'];

  function getPageNumbers(current: number, total: number) {
    const pages: (number | string)[] = [];
    if (total <= 7) {
      for (let i = 1; i <= total; i++) pages.push(i);
    } else {
      if (current <= 4) {
        pages.push(1, 2, 3, 4, 5, '...', total);
      } else if (current >= total - 3) {
        pages.push(1, '...', total - 4, total - 3, total - 2, total - 1, total);
      } else {
        pages.push(1, '...', current - 1, current, current + 1, '...', total);
      }
    }
    return pages;
  }

  function openOrder(order: any) {
    selectedOrder = order;
    isDrawerOpen = true;
  }

  function handleSave(updatedOrder: any) {
    invalidateOrders();
    if (selectedOrder && selectedOrder.id === updatedOrder.id) {
      selectedOrder = { ...selectedOrder, status: updatedOrder.status };
    }
    isDrawerOpen = false;
    addToast('Order status successfully updated.', 'success');
  }
</script>

<div class="space-y-6">
  <div class="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
    <div class="space-y-1">
      <h2 class="text-3xl font-extrabold tracking-tight text-base-content">Storefront Orders</h2>
      <p class="text-sm text-base-content/70">Monitor transactions, patron checkout details, and statuses</p>
    </div>

    <!-- Status filter -->
    <div class="flex items-center gap-2 p-1 bg-base-200 rounded-xl border border-base-300 overflow-x-auto">
      {#each STATUS_OPTIONS as status}
        <button
          onclick={() => { orderStatus = status; page = 1; }}
          class="whitespace-nowrap px-2.5 py-1.5 rounded-lg text-[11px] font-bold transition-all {orderStatus === status ? 'bg-accent text-accent-content shadow' : 'hover:bg-base-300 text-base-content/70'}"
        >
          {status === 'ALL' ? 'All' : status.charAt(0) + status.slice(1).toLowerCase()}
        </button>
      {/each}
    </div>
  </div>

  {#if ordersQueryIsLoading}
    <div class="space-y-4">
      <Skeleton height="h-12" />
      <Skeleton height="h-16" />
      <Skeleton height="h-16" />
      <Skeleton height="h-16" />
    </div>
  {:else if !orders || orders.length === 0}
    <div class="py-20 text-center border border-dashed border-base-300 rounded-xl">
      <p class="text-base-content/50 font-medium">No order files recorded on this environment.</p>
    </div>
  {:else}
    <!-- Desktop Table View -->
    <div class="hidden md:block bg-base-100 border border-base-300 rounded-xl overflow-hidden shadow-xl">
      <div class="overflow-x-auto">
        <table class="table table-zebra table-md w-full">
          <thead>
            <tr class="bg-base-200 text-xs font-semibold text-base-content/70 uppercase tracking-wider">
              <th>Order ID</th>
              <th>Customer Details</th>
              <th>Checkout Date</th>
              <th>Quantity</th>
              <th>Aggregate Total</th>
              <th>Status</th>
            </tr>
          </thead>
          <tbody class="text-base-content/90">
            {#each orders as order}
              <tr class="hover cursor-pointer transition-colors" onclick={() => openOrder(order)}>
                <td class="font-mono text-xs font-bold text-accent">
                  #{order.id.slice(-8).toUpperCase()}
                </td>
                <td>
                  <div class="font-bold text-base-content">{order.user?.name || 'Customer'}</div>
                  <div class="text-xs text-base-content/50">{order.user?.email || 'N/A'}</div>
                </td>
                <td class="text-xs font-semibold text-base-content/85">
                  {new Date(order.createdAt).toLocaleDateString(undefined, { dateStyle: 'medium' })}
                </td>
                <td class="text-xs text-base-content/65 font-semibold">
                  {order.items?.length || 0} items
                </td>
                <td class="font-bold text-base-content">
                  <span class="text-xs opacity-75 font-semibold">Rs.</span> {Number(order.total).toFixed(2)}
                </td>
                <td>
                  <Badge
                    variant={
                      order.status === 'PENDING' ? 'warning' :
                      order.status === 'PROCESSING' ? 'info' :
                      order.status === 'SHIPPED' ? 'primary' :
                      order.status === 'DELIVERED' ? 'success' :
                      'neutral'
                    }
                    size="xs"
                    class="badge-outline font-bold uppercase tracking-wider"
                  >
                    {order.status}
                  </Badge>
                </td>
              </tr>
            {/each}
          </tbody>
        </table>
      </div>

      <!-- Server-side Pagination Controls -->
      {#if ordersMeta && ordersMeta.totalPages > 1}
        <div class="flex flex-col sm:flex-row items-center justify-between gap-4 bg-base-200/50 p-4 border-t border-base-300">
          <div class="text-xs font-bold text-base-content/50 uppercase tracking-wider">
            Showing {Math.min((ordersMeta.page - 1) * ordersMeta.limit + 1, ordersMeta.total)}-{Math.min(ordersMeta.page * ordersMeta.limit, ordersMeta.total)} of {ordersMeta.total} orders
          </div>
          <div class="flex items-center gap-2">
            <button type="button" disabled={page === 1} onclick={() => page = 1} class="btn btn-outline btn-xs font-bold border-base-300 disabled:opacity-30 hover:bg-base-200">First</button>
            <button type="button" disabled={page === 1} onclick={() => page--} class="btn btn-outline btn-xs font-bold border-base-300 disabled:opacity-30 hover:bg-base-200">Prev</button>
            <div class="join">
              {#each getPageNumbers(ordersMeta.page, ordersMeta.totalPages) as p}
                {#if p === '...'}
                  <span class="join-item btn btn-xs btn-disabled font-bold border-base-300">...</span>
                {:else}
                  <button type="button" onclick={() => page = Number(p)} class="join-item btn btn-xs font-bold border-base-300 {page === p ? 'btn-primary text-primary-content' : 'hover:bg-base-200'}">{p}</button>
                {/if}
              {/each}
            </div>
            <button type="button" disabled={page === ordersMeta.totalPages} onclick={() => page++} class="btn btn-outline btn-xs font-bold border-base-300 disabled:opacity-30 hover:bg-base-200">Next</button>
            <button type="button" disabled={page === ordersMeta.totalPages} onclick={() => page = ordersMeta.totalPages} class="btn btn-outline btn-xs font-bold border-base-300 disabled:opacity-30 hover:bg-base-200">Last</button>
          </div>
        </div>
      {/if}
    </div>

    <!-- Mobile Card View -->
    <div class="md:hidden space-y-4">
      {#each orders as order}
        <!-- svelte-ignore a11y_click_events_have_key_events -->
        <!-- svelte-ignore a11y_no_noninteractive_element_interactions -->
        <div 
          class="card bg-base-100 border border-base-300 rounded-2xl p-4 shadow-sm hover:border-primary/30 transition-all duration-300 space-y-3 cursor-pointer"
          onclick={() => openOrder(order)}
        >
          <div class="flex items-center justify-between">
            <span class="font-mono text-xs font-black text-accent">
              #{order.id.slice(-8).toUpperCase()}
            </span>
            <Badge
              variant={
                order.status === 'PENDING' ? 'warning' :
                order.status === 'PROCESSING' ? 'info' :
                order.status === 'SHIPPED' ? 'primary' :
                order.status === 'DELIVERED' ? 'success' :
                'neutral'
              }
              size="xs"
              class="badge-outline font-black uppercase tracking-wider"
            >
              {order.status}
            </Badge>
          </div>

          <div class="grid grid-cols-2 gap-2 text-[11px] pt-1">
            <div>
              <span class="block text-[9px] font-bold text-base-content/40 uppercase tracking-wider mb-0.5">Customer</span>
              <span class="font-bold text-base-content block">{order.user?.name || 'Customer'}</span>
              <span class="text-base-content/50 block truncate">{order.user?.email || 'N/A'}</span>
            </div>
            <div>
              <span class="block text-[9px] font-bold text-base-content/40 uppercase tracking-wider mb-0.5">Order Date</span>
              <span class="font-semibold text-base-content/75 block">
                {new Date(order.createdAt).toLocaleDateString(undefined, { dateStyle: 'medium' })}
              </span>
            </div>
          </div>

          <div class="grid grid-cols-2 gap-2 pt-2.5 border-t border-base-200 text-[11px]">
            <div>
              <span class="block text-[9px] font-bold text-base-content/40 uppercase tracking-wider mb-0.5">Volume</span>
              <span class="font-bold text-base-content block">{order.items?.length || 0} item(s)</span>
            </div>
            <div>
              <span class="block text-[9px] font-bold text-base-content/40 uppercase tracking-wider mb-0.5">Grand Total</span>
              <span class="font-black text-xs text-accent">Rs. {Number(order.total).toFixed(2)}</span>
            </div>
          </div>
        </div>
      {/each}

      <!-- Mobile Pagination -->
      {#if ordersMeta && ordersMeta.totalPages > 1}
        <div class="bg-base-100 border border-base-300 rounded-2xl p-4 flex items-center justify-between shadow-sm">
          <button type="button" disabled={page === 1} onclick={() => page--} class="btn btn-outline btn-xs font-bold border-base-300 disabled:opacity-30 hover:bg-base-200">Prev</button>
          <div class="text-xs font-bold text-base-content">Page {page} of {ordersMeta.totalPages}</div>
          <button type="button" disabled={page === ordersMeta.totalPages} onclick={() => page++} class="btn btn-outline btn-xs font-bold border-base-300 disabled:opacity-30 hover:bg-base-200">Next</button>
        </div>
      {/if}
    </div>
  {/if}
</div>

<OrderDrawer
  bind:isOpen={isDrawerOpen}
  order={selectedOrder}
  client={client}
  onClose={() => isDrawerOpen = false}
  onSave={handleSave}
/>
